import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Breadcrumb } from './breadcrumb';
import { ReactElement, ReactNode, useState } from 'react';
import { Helmet } from 'react-helmet';
import { LoadingIndicator } from './loading-indicator.component';
import EditIcon from '@mui/icons-material/Edit';
import { BackNavigation } from './back-navigation.component';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { UserPermissionName } from '../models/user-permission-name';
import { GuardedComponent } from './guarded-component';
import { isMobile } from 'react-device-detect';

interface GridActionPermission {
  view?: UserPermissionName;
  edit?: UserPermissionName;
  delete?: UserPermissionName;
}
interface SiTrackPageProps {
  title: string;
  isLoading?: boolean;
  hideBreadcrumb?: boolean;
  hideBackNav?: boolean;
  actionButton?: ReactElement;
  actionPermissions?: GridActionPermission;
  children: ReactNode;
  onEdit?: (data: any) => void;
}

const styling = {
  dropdownStyle: { width: 250 },
  textStyle: { marginLeft: 10, width: 250 },
  headerUATColor: { backgroundColor: '#f39c12' }
};

const isMobileViewEnabled = process.env.REACT_APP_MOBILE_VIEW_FLAG === 'true';
const isUATProductionEnable = process.env.REACT_APP_UAT_PRODUCTION === 'false';

const ComponentCell = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('');
  const [searchText, setSearchText] = useState('');

  const handleChange = (event: any) => {
    setSearchType(event.target.value);
    setSearchText('');
  };

  const handleSearchTextChange = (event: any) => {
    setSearchText(event.target.value);
  };

  const redirectSearchPage = () => {
    if (searchType && searchText) {
      navigate(
        `/${searchType == 'jobsites' ? 'units' : searchType == 'invoice' ? 'workorders' : searchType}?searchType=${searchType}&searchText=${searchText}`
      );
      setSearchText('');
      setSearchType('');
    }
  };

  return (
    <>
      <FormControl fullWidth size="small" style={styling.dropdownStyle}>
        {!searchType && (
          <InputLabel size="small" id="demo-simple-select-label">
            Search Type
          </InputLabel>
        )}
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={searchType}
          label={!searchType ? 'Search Type' : ''}
          onChange={handleChange}
        >
          <MenuItem value={'projects'}>Project</MenuItem>
          <MenuItem value={'units'}>Lot/Unit</MenuItem>
          <MenuItem value={'workorders'}>Work Order#/Purchase Order#</MenuItem>
          <MenuItem value={'jobsites'}>Jobsite</MenuItem>
          <MenuItem value={'invoice'}>Invoice#</MenuItem>
        </Select>
      </FormControl>
      <TextField
        size="small"
        style={styling.textStyle}
        id="outlined-basic"
        placeholder="Search Value"
        variant="outlined"
        value={searchText}
        disabled={!searchType}
        onChange={handleSearchTextChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            redirectSearchPage();
          }
        }}
      />
      <IconButton aria-label="search" onClick={redirectSearchPage}>
        <Tooltip title={<Typography variant="body1">Search</Typography>}>
          <SearchIcon />
        </Tooltip>
      </IconButton>
    </>
  );
};
export const SiTrackPage = ({
  title,
  isLoading,
  hideBreadcrumb,
  hideBackNav,
  actionButton,
  children,
  actionPermissions,
  onEdit,
}: SiTrackPageProps) => {
  return (
    <Grid>
      <Helmet>
        <title>{'SI TRACK'}</title>
      </Helmet>
      <Grid style={isUATProductionEnable ? styling.headerUATColor : {}} className={`pageHead ${(isMobile && isMobileViewEnabled) ? 'smallDevice' : ''}`} container alignItems="center" direction="row">
        <Grid container spacing={2}>
          <Grid item lg={5} sm={5} xl={5} xs={5}>
            <Typography title={title} variant="h1" color="textPrimary" className="d-flex">
              <div>
                {!hideBackNav && (
                  <Grid>
                    <BackNavigation />
                  </Grid>
                )}
              </div>
              <div hidden={isMobile && isMobileViewEnabled}>
                {title}
                {!hideBreadcrumb && <Breadcrumb />}
              </div>
            </Typography>
          </Grid>
          <Grid item lg={7} sm={7} xl={7} xs={7}>
            <Grid container direction="row" justifyContent="flex-end" alignItems="flex-start">
              {(!isMobile || !isMobileViewEnabled) && (
                <Grid item>
                  <ComponentCell />
                </Grid>
              )}
              <Grid item style={{ marginLeft: 'auto' }}>
                {onEdit && (
                  <GuardedComponent requiredPermissions={actionPermissions?.edit ? [actionPermissions.edit] : []}>
                    <IconButton aria-label="edit" onClick={onEdit}>
                      <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                        <EditIcon className="btnEdit" />
                      </Tooltip>
                    </IconButton>
                  </GuardedComponent>
                )}
              </Grid>
              <Grid item>{actionButton ?? actionButton}</Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid className="mainContentWrapper">
        <LoadingIndicator isLoading={isLoading} isSpinner />
        {children}
      </Grid>
    </Grid>
  );
};
